import { useState } from "react";
import { Box, Chip, Divider, Grid2 as Grid, Stack, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";
import DateRangeFilter from "@/components/dashboard/DateRangeFilter";
import BarChartCard from "@/components/dashboard/BarChartCard";
import LineChartCard from "@/components/dashboard/LineChartCard";
import PieChartCard from "@/components/dashboard/PieChartCard";
import DualLineChartCard from "@/components/dashboard/DualLineChartCard";
import {
    useTopProductos,
    useClientesDistintosDia,
    useClientesNuevosDia,
    useProductosVendidosDia,
    useMontoPorVendedor,
    useUtilidadDiaria,
    useTopRubros,
    useNacionalidadClientes,
    useStockBajo,
    useVentasCreditoContado,
    useTopClientes,
    useMetodosPago,
    useDEAprobadosTipo,
    useDEEstadoSifen,
    useDEMontoDia,
    useDETopClientes,
    DashboardQuery,
} from "@/features/dashboard/api/get-dashboard-data";
import { useAuthContext } from "@/features/auth/contexts/auth-context";
import { Permiso } from "@/shared/types/permiso";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { useOperationalMode } from "@/features/configuration/hooks/use-operational-mode";
import { DateValue, DateIntValue, CreditoContadoDia } from "@/features/dashboard/api/get-dashboard-data";

function groupByMonth<T extends { date: string }>(
    data: T[],
    aggregate: (items: T[]) => Omit<T, "date">,
): T[] {
    const map = new Map<string, T[]>();
    for (const item of data) {
        const key = dayjs(item.date).format("YYYY-MM-01");
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(item);
    }
    return Array.from(map.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, items]) => ({ date: key, ...aggregate(items) } as T));
}

function groupDateValues(data: DateValue[]): DateValue[] {
    return groupByMonth(data, (items) => ({ value: items.reduce((s, i) => s + i.value, 0) }));
}

function groupDateIntValues(data: DateIntValue[]): DateIntValue[] {
    return groupByMonth(data, (items) => ({ value: items.reduce((s, i) => s + i.value, 0) }));
}

function groupCreditoContado(data: CreditoContadoDia[]): CreditoContadoDia[] {
    return groupByMonth(data, (items) => ({
        credito: items.reduce((s, i) => s + i.credito, 0),
        contado: items.reduce((s, i) => s + i.contado, 0),
    }));
}

const COLORS = {
    volume: "#635bff",
    growth: "#15b79f",
    money: "#fb9c0c",
    info: "#04aad6",
};

export default function HomePage() {
    const [desde, setDesde] = useState<Dayjs>(dayjs().startOf("month"));
    const [hasta, setHasta] = useState<Dayjs>(dayjs().endOf("month"));
    const { hasPermisos } = useAuthContext();
    const { monedaBase } = useMonetizacion();

    const q: DashboardQuery = {
        desde: desde.format("YYYY-MM-DD"),
        hasta: hasta.format("YYYY-MM-DD"),
    };

    const isRootAdmin = hasPermisos(Permiso.ManageOrganizaciones);
    const canSeeFinanciero = hasPermisos(Permiso.ReadDashboardFinanciero);
    const { gestionEnabled, facturacionEnabled } = useOperationalMode();

    if (isRootAdmin) return null;

    const topProductos = useTopProductos(q);
    const clientesDistintos = useClientesDistintosDia(q);
    const clientesNuevos = useClientesNuevosDia(q);
    const productosVendidos = useProductosVendidosDia(q);
    const montoPorVendedor = useMontoPorVendedor(q, canSeeFinanciero);
    const utilidadDiaria = useUtilidadDiaria(q, canSeeFinanciero);
    const topRubros = useTopRubros(q);
    const nacionalidad = useNacionalidadClientes(q);
    const stockBajo = useStockBajo();
    const ventasCreditoContado = useVentasCreditoContado(q, canSeeFinanciero);
    const topClientes = useTopClientes(q, canSeeFinanciero);
    const metodosPago = useMetodosPago(q, canSeeFinanciero);
    const deAprobadosTipo = useDEAprobadosTipo(q, facturacionEnabled);
    const deEstadoSifen = useDEEstadoSifen(q, facturacionEnabled);
    const deMontoDia = useDEMontoDia(q, facturacionEnabled && canSeeFinanciero);
    const deTopClientes = useDETopClientes(q, facturacionEnabled && canSeeFinanciero);

    const isWideRange = hasta.diff(desde, "day") > 60;
    const dateFormat = isWideRange ? "MMM YY" : "DD/MM";

    const periodLabel = `${desde.format("D MMM")} – ${hasta.format("D MMM YYYY")}`;

    return (
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, maxWidth: 1400, mx: "auto" }}>

            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "baseline" }} spacing={1.5} mb={2.5}>
                <Typography variant="h5" fontWeight={500}>Resumen del negocio</Typography>
                <Typography variant="body2" color="text.secondary">{periodLabel}</Typography>
            </Stack>

            <DateRangeFilter desde={desde} hasta={hasta} onDesdeChange={setDesde} onHastaChange={setHasta} />

            <Grid container spacing={2} sx={{ mt: 2 }}>

                {gestionEnabled && (
                    <>
                        {/* Fila 1: tendencia principal (md:8) + composición por rubro (md:4) */}
                        <Grid size={{ xs: 12, md: 8 }}>
                            <LineChartCard
                                title={isWideRange ? "Productos vendidos por mes" : "Productos vendidos por día"}
                                data={isWideRange && productosVendidos.data ? groupDateValues(productosVendidos.data) : productosVendidos.data}
                                loading={productosVendidos.isLoading} color={COLORS.volume} dateFormat={dateFormat}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <PieChartCard title="Rubros más vendidos" data={topRubros.data} loading={topRubros.isLoading} />
                        </Grid>

                        {/* Fila 2: ranking productos + clientes distintos */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <BarChartCard title="Top 10 productos más vendidos" data={topProductos.data} loading={topProductos.isLoading} horizontal color={COLORS.volume} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <LineChartCard
                                title={isWideRange ? "Clientes distintos por mes" : "Clientes distintos por día"}
                                data={isWideRange && clientesDistintos.data ? groupDateIntValues(clientesDistintos.data) : clientesDistintos.data}
                                loading={clientesDistintos.isLoading} color={COLORS.info} dateFormat={dateFormat}
                            />
                        </Grid>
                    </>
                )}

                {/* Clientes nuevos — visible en todos los modos */}
                <Grid size={{ xs: 12, md: gestionEnabled ? 4 : 6 }}>
                    <LineChartCard
                        title={isWideRange ? "Clientes nuevos por mes" : "Clientes nuevos por día"}
                        data={isWideRange && clientesNuevos.data ? groupDateIntValues(clientesNuevos.data) : clientesNuevos.data}
                        loading={clientesNuevos.isLoading} color={COLORS.growth} dateFormat={dateFormat}
                    />
                </Grid>

                {/* Financiero — solo en modo gestión */}
                {gestionEnabled && canSeeFinanciero && (
                    <>
                        {/* Fila 3: utilidad — full width */}
                        <Grid size={{ xs: 12 }}>
                            <LineChartCard
                                title={isWideRange ? "Utilidad por mes" : "Utilidad diaria"}
                                data={isWideRange && utilidadDiaria.data ? groupDateValues(utilidadDiaria.data) : utilidadDiaria.data}
                                loading={utilidadDiaria.isLoading} color={COLORS.growth} currencySymbol={monedaBase?.simbolo} dateFormat={dateFormat}
                            />
                        </Grid>

                        {/* Fila 4: crédito vs contado (md:8) + métodos de pago (md:4) */}
                        <Grid size={{ xs: 12, md: 8 }}>
                            <DualLineChartCard
                                title={isWideRange ? "Ventas crédito vs contado por mes" : "Ventas crédito vs contado por día"}
                                data={isWideRange && ventasCreditoContado.data ? groupCreditoContado(ventasCreditoContado.data) : ventasCreditoContado.data}
                                loading={ventasCreditoContado.isLoading}
                                field1="credito" field2="contado"
                                label1="Crédito" label2="Contado"
                                color1={COLORS.money} color2={COLORS.volume}
                                currencySymbol={monedaBase?.simbolo}
                                dateFormat={dateFormat}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <PieChartCard title="Métodos de pago" data={metodosPago.data} loading={metodosPago.isLoading} />
                        </Grid>

                        {/* Fila 5: rankings de dinero — top clientes + monto por vendedor */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <BarChartCard title="Top 10 clientes por monto" data={topClientes.data} loading={topClientes.isLoading} horizontal color={COLORS.info} currencySymbol={monedaBase?.simbolo} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <BarChartCard title="Top 10 vendedores por monto" data={montoPorVendedor.data} loading={montoPorVendedor.isLoading} horizontal color={COLORS.money} currencySymbol={monedaBase?.simbolo} />
                        </Grid>
                    </>
                )}

                {/* ── Sección Facturación Electrónica — modos 2 y 3 ── */}
                {facturacionEnabled && (
                    <>
                        <Grid size={12}>
                            <Divider sx={{ mt: 1 }}>
                                <Chip
                                    label="Facturación Electrónica"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        fontSize: "0.7rem",
                                        fontWeight: 500,
                                        letterSpacing: "0.04em",
                                        textTransform: "uppercase",
                                        color: "text.secondary",
                                        borderColor: "divider",
                                        px: 0.5,
                                    }}
                                />
                            </Divider>
                        </Grid>

                        {/* DEs aprobados por tipo (md:4) + Estado SIFEN (md:4) */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <PieChartCard title="DEs aprobados por tipo" data={deAprobadosTipo.data} loading={deAprobadosTipo.isLoading} showCount />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <PieChartCard title="Estado SIFEN" data={deEstadoSifen.data} loading={deEstadoSifen.isLoading} showCount />
                        </Grid>

                        {/* Financiero FE */}
                        {canSeeFinanciero && (
                            <>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <BarChartCard title="Top 10 clientes (FE)" data={deTopClientes.data} loading={deTopClientes.isLoading} horizontal color={COLORS.info} currencySymbol={monedaBase?.simbolo} />
                                </Grid>
                                <Grid size={{ xs: 12, md: 8 }}>
                                    <LineChartCard
                                        title={isWideRange ? "Monto facturado por mes" : "Monto facturado por día"}
                                        data={isWideRange && deMontoDia.data ? groupDateValues(deMontoDia.data) : deMontoDia.data}
                                        loading={deMontoDia.isLoading}
                                        color={COLORS.money}
                                        currencySymbol={monedaBase?.simbolo}
                                        dateFormat={dateFormat}
                                    />
                                </Grid>
                            </>
                        )}
                    </>
                )}

                {/* ── Separador: datos sin filtro de fecha — solo en modo gestión ── */}
                {gestionEnabled && (
                    <Grid size={12}>
                        <Divider sx={{ mt: 1 }}>
                            <Chip
                                label="Estado actual"
                                size="small"
                                variant="outlined"
                                sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 500,
                                    letterSpacing: "0.04em",
                                    textTransform: "uppercase",
                                    color: "text.secondary",
                                    borderColor: "divider",
                                    px: 0.5,
                                }}
                            />
                        </Divider>
                    </Grid>
                )}

                {/* Stock bajo — solo en modo gestión */}
                {gestionEnabled && (
                    <Grid size={{ xs: 12, md: 4 }}>
                        <BarChartCard title="Top 10 productos con menos stock" data={stockBajo.data} loading={stockBajo.isLoading} horizontal color="#ef5350" />
                    </Grid>
                )}

                {/* Nacionalidad de clientes — visible en todos los modos */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <PieChartCard title="Nacionalidad de clientes" data={nacionalidad.data} loading={nacionalidad.isLoading} showCount />
                </Grid>

            </Grid>

        </Box>
    );
}
